// The All page's query language: a small JQL-like line parsed on the device.
//
//   state:next project:alfieOS due<7d -label:waiting-on sort:-updated
//   kind:task (priority>=2 OR due:today) "buy milk"
//
// A term is `field<op>value`; a bare word or "quoted phrase" searches names and
// notes; terms AND together by default, OR / AND / NOT (and a leading -) and
// parentheses combine them. `parseQuery` never throws: a bad line is an error
// with the place it went wrong, so the page can say so instead of crashing.
import { addDays, dateOf, isDate, parseDate } from './time'

export const OPS = [':', '=', '!=', '>=', '<=', '>', '<'] as const
export type Op = (typeof OPS)[number]

export type Node =
  | { t: 'all' }
  | { t: 'and'; nodes: Node[] }
  | { t: 'or'; nodes: Node[] }
  | { t: 'not'; node: Node }
  | { t: 'text'; text: string }
  | { t: 'field'; field: Field; op: Op; values: string[] }

export const KINDS = ['task', 'project', 'page', 'list'] as const
export type ItemKind = (typeof KINDS)[number]

export type SortKey = 'due' | 'priority' | 'updated' | 'created' | 'name'
export interface Sort {
  key: SortKey
  desc: boolean
}

export interface Parsed {
  ok: true
  node: Node
  sort?: Sort
}
export interface ParseError {
  ok: false
  message: string
  /** where in the text it went wrong */
  at: number
}

type FieldType = 'text' | 'number' | 'date'
interface FieldDef {
  type: FieldType
  hint: string
}

/** Every field, with the kind of value it takes. */
export const FIELDS = {
  kind: { type: 'text', hint: 'task, project, page or list' },
  state: { type: 'text', hint: 'next, waiting, inbox, someday, done, active …' },
  project: { type: 'text', hint: 'a project’s name, or none' },
  ws: { type: 'text', hint: 'a suite’s slug' },
  label: { type: 'text', hint: 'a label, or key=value' },
  is: { type: 'text', hint: 'recurring or pinned' },
  priority: { type: 'number', hint: '0–3, or none, low, medium, high' },
  due: { type: 'date', hint: 'today, none, 7d, 2026-10-31' },
  scheduled: { type: 'date', hint: 'today, none, 7d, 2026-10-31' },
  updated: { type: 'date', hint: '-3d, today, 2026-10-01' },
  created: { type: 'date', hint: '-3d, today, 2026-10-01' },
  sort: { type: 'text', hint: 'due, priority, updated, created or name; - flips it' },
} as const satisfies Record<string, FieldDef>
export type Field = keyof typeof FIELDS
export const FIELD_NAMES = Object.keys(FIELDS) as Field[]

export const TASK_STATE_WORDS = ['inbox', 'next', 'waiting', 'someday', 'done', 'dropped']
export const PROJECT_STATE_WORDS = ['active', 'paused', 'done', 'dropped']
/** What `state:` accepts: the states themselves and three groupings. */
export const STATE_VALUES = [
  ...new Set([...TASK_STATE_WORDS, ...PROJECT_STATE_WORDS]),
  'open',
  'closed',
  'any',
]
export const IS_VALUES = ['recurring', 'pinned']
export const SORT_KEYS: SortKey[] = ['due', 'priority', 'updated', 'created', 'name']
export const DATE_WORDS = ['today', 'tomorrow', 'yesterday', 'none']
export const PRIORITY_WORDS: Record<string, number> = { none: 0, low: 1, medium: 2, med: 2, high: 3 }

/** Fields that take only `:`, `=` and `!=`. */
const isEquality = (op: Op) => op === ':' || op === '=' || op === '!='

// ---- values ---------------------------------------------------------------

/** `today`, `tomorrow`, `yesterday`, `-3d`, `+2w`, `1m` or `2026-10-31` as a local date; `none` stays `none`; null if it is none of these. */
export const resolveDate = (v: string, today: string): string | 'none' | null => {
  const w = v.toLowerCase()
  if (w === 'none') return 'none'
  if (w === 'today') return today
  if (w === 'tomorrow') return addDays(today, 1)
  if (w === 'yesterday') return addDays(today, -1)
  if (isDate(w)) return dateOf(parseDate(w)) === w ? w : null
  const m = /^([+-]?)(\d{1,4})([dwm])$/.exec(w)
  if (!m) return null
  const n = Number(m[2]) * (m[1] === '-' ? -1 : 1)
  if (m[3] === 'd') return addDays(today, n)
  if (m[3] === 'w') return addDays(today, n * 7)
  const d = parseDate(today)
  d.setMonth(d.getMonth() + n)
  return dateOf(d)
}

export const priorityOf = (v: string): number | null => {
  const w = v.toLowerCase()
  if (w in PRIORITY_WORDS) return PRIORITY_WORDS[w]!
  return /^[0-3]$/.test(w) ? Number(w) : null
}

export const kindOf = (v: string): ItemKind | null => {
  const w = v.toLowerCase().replace(/s$/, '')
  return (KINDS as readonly string[]).includes(w) ? (w as ItemKind) : null
}

export const parseSort = (v: string): Sort | null => {
  const desc = v.startsWith('-')
  const key = (desc ? v.slice(1) : v).toLowerCase() as SortKey
  if (!SORT_KEYS.includes(key)) return null
  // the way each reads best: dates soonest first, priority and recency biggest first
  const natural = key === 'priority' || key === 'updated' || key === 'created'
  return { key, desc: natural ? !desc : desc }
}

const list = (words: readonly string[]) => words.join(', ')

/** The reason a value is wrong for its field, or null when it is fine. */
const badValue = (field: Field, op: Op, v: string): string | null => {
  const w = v.toLowerCase()
  switch (field) {
    case 'kind':
      return kindOf(v) ? null : `“${v}” is not a kind. Use ${list(KINDS)}.`
    case 'state':
      return STATE_VALUES.includes(w) ? null : `“${v}” is not a state. Use ${list(STATE_VALUES)}.`
    case 'is':
      return IS_VALUES.includes(w) ? null : `“${v}” does not go after is:. Use ${list(IS_VALUES)}.`
    case 'priority':
      return priorityOf(v) === null ? `“${v}” is not a priority. Use 0–3, or none, low, medium, high.` : null
    case 'due':
    case 'scheduled':
    case 'updated':
    case 'created': {
      const r = resolveDate(v, '2000-01-01')
      if (r === null) return `“${v}” is not a day. Use today, tomorrow, -3d, 2w, none or 2026-10-31.`
      if (r === 'none' && (!isEquality(op) || field === 'updated' || field === 'created'))
        return field === 'updated' || field === 'created'
          ? `${field} always has a day, so “none” does not fit.`
          : `“none” only goes with : or =, as in ${field}:none.`
      return null
    }
    default:
      return null
  }
}

// ---- the parser -----------------------------------------------------------

class Fail extends Error {
  constructor(
    message: string,
    readonly at: number,
  ) {
    super(message)
  }
}

const FIELD_AT = /^([A-Za-z][A-Za-z_]*)(!=|>=|<=|:|=|>|<)/
const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c)

/** Parse a query line. Never throws. */
export const parseQuery = (src: string): Parsed | ParseError => {
  let i = 0
  let sort: Sort | undefined
  let sorts = 0

  const skip = () => {
    while (i < src.length && /\s/.test(src[i]!)) i++
  }
  const keyword = (w: string) => {
    if (!src.startsWith(w, i)) return false
    const after = src[i + w.length]
    return isSpace(after) || after === '(' || (w === 'NOT' && after === '-')
  }

  const quoted = (): string => {
    const start = i
    i++
    let out = ''
    while (i < src.length && src[i] !== '"') out += src[i++]
    if (i >= src.length) throw new Fail('This quote is never closed. Add a closing ".', start)
    i++
    return out
  }

  const value = (field: Field, op: Op, at: number): string[] => {
    const out: string[] = []
    const read = () => {
      let cur = ''
      while (i < src.length && !/[\s,)"]/.test(src[i]!)) cur += src[i++]
      if (src[i] === '"' && !cur) cur = quoted()
      return cur
    }
    if (src[i] === '(') {
      const open = i
      i++
      for (;;) {
        const v = read()
        if (v) out.push(v)
        if (src[i] === ',') {
          i++
          continue
        }
        if (src[i] === ')') {
          i++
          break
        }
        throw new Fail('This ( is never closed. Add a ) after the last value.', open)
      }
    } else {
      for (;;) {
        const v = read()
        if (v) out.push(v)
        if (src[i] === ',') {
          i++
          continue
        }
        break
      }
    }
    if (!out.length) throw new Fail(`Nothing after ${field}${op}. Add a value, like ${example(field)}.`, at)
    return out
  }

  const term = (): Node => {
    const at = i
    if (src[i] === '"') {
      const text = quoted()
      return text.trim() ? { t: 'text', text } : { t: 'all' }
    }
    const m = FIELD_AT.exec(src.slice(i))
    if (m) {
      const name = m[1]!.toLowerCase()
      const op = m[2] as Op
      if (!(name in FIELDS))
        throw new Fail(
          `I don’t know the field “${m[1]}”. Fields: ${list(FIELD_NAMES)}. To search for the words, put them in quotes.`,
          at,
        )
      const field = name as Field
      const def: FieldDef = FIELDS[field]
      i += m[0].length
      if (def.type === 'text' && !isEquality(op))
        throw new Fail(`${field} takes : or =, not ${op}. Try ${field}:… or -${field}:….`, at)
      const values = value(field, op, at)
      for (const v of values) {
        const bad = badValue(field, op, v)
        if (bad) throw new Fail(bad, at)
      }
      if (field === 'sort') {
        if (values.length > 1) throw new Fail('sort takes one thing, like sort:due or sort:-updated.', at)
        const s = parseSort(values[0]!)
        if (!s) throw new Fail(`“${values[0]}” is not a sort. Use ${list(SORT_KEYS)}; a - flips it.`, at)
        if (op !== ':' && op !== '=') throw new Fail('sort takes : or =.', at)
        sort = s
        sorts++
        return { t: 'all' }
      }
      return { t: 'field', field, op, values }
    }
    let text = ''
    while (i < src.length && !/[\s()"]/.test(src[i]!)) text += src[i++]
    if (!text) throw new Fail('I can’t read this.', at)
    return { t: 'text', text }
  }

  const unary = (): Node => {
    skip()
    const at = i
    if (keyword('NOT')) {
      i += 3
      return negate(at)
    }
    if (src[i] === '-') {
      if (isSpace(src[i + 1]) || src[i + 1] === ')')
        throw new Fail('Put the word right after the -, like -label:later.', at)
      i++
      return negate(at)
    }
    if (src[i] === '(') {
      i++
      const inner = or()
      skip()
      if (src[i] !== ')') throw new Fail('This ( is never closed. Add a ).', at)
      i++
      return inner
    }
    return term()
  }
  const negate = (at: number): Node => {
    const before = sorts
    const node = unary()
    if (sorts !== before) throw new Fail('sort can’t be negated or used with NOT.', at)
    return { t: 'not', node }
  }

  // what the last and() read: terms of any kind, and terms that filter (not sort:)
  let raw = 0
  let real = 0
  const and = (): Node => {
    const nodes: Node[] = []
    let seen = 0
    for (;;) {
      skip()
      if (i >= src.length || src[i] === ')' || keyword('OR')) break
      if (keyword('AND')) {
        const at = i
        i += 3
        skip()
        if (!seen) throw new Fail('AND needs something before it.', at)
        if (i >= src.length || src[i] === ')' || keyword('OR') || keyword('AND'))
          throw new Fail('AND needs something after it.', at)
        continue
      }
      seen++
      nodes.push(unary())
    }
    const kept = nodes.filter((n) => n.t !== 'all')
    raw = seen
    real = kept.length
    if (kept.length === 0) return { t: 'all' }
    return kept.length === 1 ? kept[0]! : { t: 'and', nodes: kept }
  }

  const or = (): Node => {
    const nodes: Node[] = []
    const side = (at: number, which: 'before' | 'after') => {
      if (raw === 0) throw new Fail(`OR needs something ${which} it.`, at)
      if (real === 0) throw new Fail('sort goes beside the OR, not as one side of it.', at)
    }
    nodes.push(and())
    skip()
    let first = true
    while (keyword('OR')) {
      const at = i
      if (first) side(at, 'before')
      first = false
      i += 2
      nodes.push(and())
      side(at, 'after')
      skip()
    }
    return nodes.length === 1 ? nodes[0]! : { t: 'or', nodes }
  }

  try {
    const node = or()
    skip()
    if (i < src.length)
      throw new Fail(src[i] === ')' ? 'This ) has no ( before it.' : 'I can’t read this.', i)
    return { ok: true, node, sort }
  } catch (e) {
    if (e instanceof Fail) return { ok: false, message: e.message, at: e.at }
    return { ok: false, message: 'I can’t read this query.', at: 0 }
  }
}

const example = (f: Field): string =>
  ({
    kind: 'kind:task',
    state: 'state:next',
    project: 'project:alfieOS',
    ws: 'ws:devos',
    label: 'label:home',
    is: 'is:recurring',
    priority: 'priority>=2',
    due: 'due<7d',
    scheduled: 'scheduled:none',
    updated: 'updated>-3d',
    created: 'created>-1w',
    sort: 'sort:due',
  })[f]

// ---- reading a query ---------------------------------------------------------

/** Every field term in the tree, with whether it sits under an odd number of NOTs. */
export const fieldTerms = (
  node: Node,
  negated = false,
): { term: Extract<Node, { t: 'field' }>; negated: boolean }[] => {
  switch (node.t) {
    case 'field':
      return [{ term: node, negated }]
    case 'not':
      return fieldTerms(node.node, !negated)
    case 'and':
    case 'or':
      return node.nodes.flatMap((n) => fieldTerms(n, negated))
    default:
      return []
  }
}

/**
 * What the query asks for beyond the open things: done or dropped items (a
 * `state:` term, not negated, naming done, dropped, closed or any), recurring
 * templates (`is:recurring`), and which kinds, if it says.
 */
export const reach = (node: Node) => {
  const terms = fieldTerms(node)
  const states = terms.filter((t) => t.term.field === 'state' && !t.negated && t.term.op !== '!=')
  const closed = states.some((t) =>
    t.term.values.some((v) => ['done', 'dropped', 'closed', 'any'].includes(v.toLowerCase())),
  )
  const templates = terms.some(
    (t) => t.term.field === 'is' && !t.negated && t.term.values.some((v) => v.toLowerCase() === 'recurring'),
  )
  const suite = terms.some((t) => t.term.field === 'ws')
  const kinds = new Set<ItemKind>()
  for (const t of terms)
    if (t.term.field === 'kind' && !t.negated && t.term.op !== '!=')
      for (const v of t.term.values) kinds.add(kindOf(v)!)
  return { closed, templates, suite, kinds }
}

// ---- matching -------------------------------------------------------------

/** One thing, flattened to what the query can ask about. */
export interface Subject {
  kind: ItemKind
  id: string
  title: string
  /** notes, page text, list items: searched by free words */
  body: string
  /** a task's state, a project's state; pages and lists have none */
  state?: string
  open: boolean
  template: boolean
  pinned: boolean
  /** the project it belongs to, as its title */
  project?: string
  ws: string
  labels: Record<string, string>
  priority?: number
  due?: string
  scheduled?: string
  updated: string
  created: string
}

/** Lowercase, letters and digits only: `Alfie OS`, `alfie-os` and `alfieOS` meet. */
export const squash = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '')

const labelHit = (labels: Record<string, string>, v: string): boolean => {
  const w = v.toLowerCase()
  const sep = w.search(/[=:]/)
  if (sep > 0) {
    const key = w.slice(0, sep)
    const val = w.slice(sep + 1)
    return Object.entries(labels).some(([k, x]) => k.toLowerCase() === key && x.toLowerCase() === val)
  }
  return Object.entries(labels).some(([k, x]) => k.toLowerCase() === w || x.toLowerCase() === w)
}

const compare = (a: number | string, op: Op, b: number | string): boolean => {
  switch (op) {
    case ':':
    case '=':
      return a === b
    case '!=':
      return a !== b
    case '>':
      return a > b
    case '>=':
      return a >= b
    case '<':
      return a < b
    case '<=':
      return a <= b
  }
}

/** Whether one field term holds for a subject. A missing value matches nothing, except `!=`. */
const fieldHolds = (s: Subject, f: Extract<Node, { t: 'field' }>, today: string): boolean => {
  const { field, op, values } = f
  const some = (hit: (v: string) => boolean) => values.some(hit)
  // a != list says none of them
  const eq = (hit: (v: string) => boolean) => (op === '!=' ? !some(hit) : some(hit))
  switch (field) {
    case 'kind':
      return eq((v) => kindOf(v) === s.kind)
    case 'state':
      return eq((v) => {
        const w = v.toLowerCase()
        if (w === 'any') return s.state !== undefined
        if (w === 'open') return s.state !== undefined && s.open
        if (w === 'closed') return s.state !== undefined && !s.open
        return s.state === w
      })
    case 'project':
      return eq((v) =>
        v.toLowerCase() === 'none' ? !s.project : !!s.project && squash(s.project).includes(squash(v)),
      )
    case 'ws':
      return eq((v) => s.ws === v.toLowerCase())
    case 'label':
      return eq((v) => labelHit(s.labels, v))
    case 'is':
      return eq((v) => (v.toLowerCase() === 'recurring' ? s.template : s.pinned))
    case 'priority': {
      if (s.priority === undefined) return op === '!='
      const p = s.priority
      return op === '!=' ? !some((v) => p === priorityOf(v)) : some((v) => compare(p, op, priorityOf(v)!))
    }
    case 'due':
    case 'scheduled':
    case 'updated':
    case 'created': {
      const have = s[field]
      const day = (v: string) => resolveDate(v, today)!
      if (op === '!=') return !some((v) => (day(v) === 'none' ? !have : have === day(v)))
      if (op === ':' || op === '=') return some((v) => (day(v) === 'none' ? !have : have === day(v)))
      return !!have && some((v) => compare(have, op, day(v)))
    }
    case 'sort':
      return true
  }
}

export const matches = (node: Node, s: Subject, today: string): boolean => {
  switch (node.t) {
    case 'all':
      return true
    case 'and':
      return node.nodes.every((n) => matches(n, s, today))
    case 'or':
      return node.nodes.some((n) => matches(n, s, today))
    case 'not':
      return !matches(node.node, s, today)
    case 'text': {
      const w = node.text.toLowerCase()
      return s.title.toLowerCase().includes(w) || s.body.toLowerCase().includes(w)
    }
    case 'field':
      return fieldHolds(s, node, today)
  }
}

// ---- running ----------------------------------------------------------------

export interface RunOptions {
  today: string
  /** the suite the page is in: `*` is Everything. A `ws:` term in the query lifts it. */
  scope: string
  /** kinds the tab is restricted to; the query's own `kind:` narrows further */
  kinds?: readonly ItemKind[]
}

const sortValue = (s: Subject, key: SortKey): number | string | undefined =>
  key === 'name' ? s.title.toLowerCase() : key === 'priority' ? s.priority : s[key]

/** Order subjects: missing values last whichever way it runs, then by recent change, then name. */
export const sortSubjects = (subjects: Subject[], sort: Sort): Subject[] =>
  [...subjects].sort((a, b) => {
    const x = sortValue(a, sort.key)
    const y = sortValue(b, sort.key)
    if (x === undefined && y !== undefined) return 1
    if (y === undefined && x !== undefined) return -1
    if (x !== undefined && y !== undefined && x !== y) return (x < y ? -1 : 1) * (sort.desc ? -1 : 1)
    return b.updated.localeCompare(a.updated) || a.title.localeCompare(b.title)
  })

/** The default order: most recently changed first. */
export const DEFAULT_SORT: Sort = { key: 'updated', desc: true }

/**
 * The subjects a query picks, grouped by kind. Open things only unless the
 * query asks for done or dropped ones; recurring templates only on
 * `is:recurring`; the suite's things only unless the query names a `ws:`.
 */
export const runQuery = (p: Parsed, subjects: Subject[], o: RunOptions): Record<ItemKind, Subject[]> => {
  const r = reach(p.node)
  const out: Record<ItemKind, Subject[]> = { task: [], project: [], page: [], list: [] }
  const allowed = o.kinds ? new Set<ItemKind>(o.kinds) : null
  for (const s of subjects) {
    if (allowed && !allowed.has(s.kind)) continue
    if (!r.suite && o.scope !== '*' && s.ws !== o.scope) continue
    if (s.template && !r.templates) continue
    if (!s.open && !r.closed) continue
    if (matches(p.node, s, o.today)) out[s.kind].push(s)
  }
  const sort = p.sort ?? DEFAULT_SORT
  for (const k of KINDS) out[k] = sortSubjects(out[k], sort)
  return out
}

// ---- autocomplete ---------------------------------------------------------------

export interface SuggestContext {
  projects: string[]
  suites: string[]
  labels: string[]
}
export interface Suggestion {
  /** what the chip says */
  label: string
  /** what replaces text[from, to] */
  insert: string
  from: number
  to: number
}

const valuesFor = (field: Field, c: SuggestContext): string[] => {
  switch (field) {
    case 'kind':
      return [...KINDS]
    case 'state':
      return STATE_VALUES
    case 'is':
      return IS_VALUES
    case 'project':
      return ['none', ...c.projects]
    case 'ws':
      return c.suites
    case 'label':
      return c.labels
    case 'priority':
      return ['0', '1', '2', '3', 'high']
    case 'due':
    case 'scheduled':
      return ['today', 'tomorrow', 'none', '7d', '-3d', 'yesterday']
    case 'updated':
    case 'created':
      return ['today', '-1d', '-3d', '-1w', '-1m']
    case 'sort':
      return SORT_KEYS.flatMap((k) => [k, `-${k}`])
  }
}

/** A value as typed: a name with spaces or marks is squashed, which `project:` and the rest still find. */
const typed = (v: string) => (/^[\w.=:-]+$/.test(v) ? v : squash(v) || v)

/**
 * Chips for the word under the caret: field names while it has no operator,
 * values for the field once it has one. Empty when nothing fits.
 */
export const suggest = (text: string, caret: number, c: SuggestContext, max = 12): Suggestion[] => {
  const head = text.slice(0, caret)
  const wsAt = Math.max(head.lastIndexOf(' '), head.lastIndexOf('\n'), head.lastIndexOf('\t'))
  let start = wsAt + 1
  let seg = head.slice(start)
  // leading ( and - belong to the grouping, not the word
  while (/^[(-]/.test(seg)) {
    seg = seg.slice(1)
    start++
  }
  const m = FIELD_AT.exec(seg)
  if (m && m[1]!.toLowerCase() in FIELDS) {
    const field = m[1]!.toLowerCase() as Field
    let rest = seg.slice(m[0].length)
    let restStart = start + m[0].length
    if (rest.startsWith('(')) {
      rest = rest.slice(1)
      restStart++
    }
    const comma = rest.lastIndexOf(',')
    if (comma >= 0) {
      rest = rest.slice(comma + 1)
      restStart += comma + 1
    }
    const w = rest.toLowerCase()
    // a value already in the term is not offered again
    const used = new Set(
      seg
        .slice(m[0].length)
        .split(/[(,]/)
        .slice(0, -1)
        .map((x) => x.toLowerCase()),
    )
    const fit = valuesFor(field, c)
      .filter(
        (v) => v.toLowerCase().startsWith(w) || (field === 'project' && squash(v).startsWith(squash(w))),
      )
      .filter((v) => !used.has(typed(v).toLowerCase()) && typed(v).toLowerCase() !== w)
    return fit.slice(0, max).map((v) => ({ label: v, insert: `${typed(v)} `, from: restStart, to: caret }))
  }
  if (/[:=<>]/.test(seg)) return []
  const w = seg.toLowerCase()
  const fields = FIELD_NAMES.filter((f) => f.startsWith(w))
  // the joining words offer themselves once typed in capitals
  const words = seg && seg === seg.toUpperCase() ? ['AND', 'OR', 'NOT'].filter((k) => k.startsWith(seg)) : []
  return [
    ...fields.map((f) => ({
      label: f,
      insert: `${f}:`,
      from: start,
      to: caret,
    })),
    ...words.map((k) => ({ label: k, insert: `${k} `, from: start, to: caret })),
  ].slice(0, max)
}

/** Apply a chip to the text; the caret goes to the end of what was inserted. */
export const applySuggestion = (text: string, s: Suggestion): { text: string; caret: number } => {
  const after = text.slice(s.to)
  const insert = after.startsWith(' ') && s.insert.endsWith(' ') ? s.insert.trimEnd() : s.insert
  return { text: text.slice(0, s.from) + insert + after, caret: s.from + insert.length }
}
